import { getCampaignStatusAPI } from 'api/campaign'
import { Status } from 'api/common/statuses'
import * as React from 'react'

const POLLING_INTERVAL_MS = 2000 as const
const LONG_MESSAGE_MS = 15000 as const
const TIMEOUT_MS = 30000 as const
const REQUIRED_BINARY_MAP_COUNT = 3 as const

export interface CampaignStatusState {
  status: 'processing' | 'completed' | 'timeout' | 'error'
  binaryMapCount: number
  showLongRunningMessage: boolean
}

const initialState: CampaignStatusState = {
  status: 'processing',
  binaryMapCount: 0,
  showLongRunningMessage: false,
}

/**
 * キャンペーン参加状況ポーリング フック (要件書 §7.5).
 *
 * トークン消費後の Stage B トランザクション (BinaryMap×3 生成) を
 * `GET /campaign/status/:token` でポーリングする.
 *
 * - {@link POLLING_INTERVAL_MS} (2秒) ごとに API 呼出
 * - `binaryMapCount === 3` または response.status === 'completed' で停止
 * - {@link LONG_MESSAGE_MS} (15秒) で showLongRunningMessage=true
 * - {@link TIMEOUT_MS} (30秒) で status='timeout' (内部 CRON は継続するためエラー扱いではない)
 */
export const useCampaignStatusPolling = (token: string | undefined): CampaignStatusState => {
  const [state, setState] = React.useState<CampaignStatusState>(initialState)
  const stoppedRef = React.useRef<boolean>(false)
  const intervalRef = React.useRef<ReturnType<typeof setInterval> | null>(null)
  const longMessageTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)
  const timeoutTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null)

  const stopPolling = React.useCallback(() => {
    stoppedRef.current = true
    if (intervalRef.current) {
      clearInterval(intervalRef.current)
      intervalRef.current = null
    }
    if (longMessageTimerRef.current) {
      clearTimeout(longMessageTimerRef.current)
      longMessageTimerRef.current = null
    }
    if (timeoutTimerRef.current) {
      clearTimeout(timeoutTimerRef.current)
      timeoutTimerRef.current = null
    }
  }, [])

  const fetchStatus = React.useCallback(async (currentToken: string) => {
    if (stoppedRef.current) return
    const result = await getCampaignStatusAPI(currentToken)
    if (stoppedRef.current) return
    if (result.status !== Status.Success || !result.response) {
      return
    }
    const { status: serverStatus, binaryMapCount } = result.response
    if (serverStatus === 'completed' || binaryMapCount >= REQUIRED_BINARY_MAP_COUNT) {
      setState((prev) => ({ ...prev, status: 'completed', binaryMapCount }))
      stopPolling()
      return
    }
    setState((prev) => ({ ...prev, binaryMapCount }))
  }, [stopPolling])

  React.useEffect(() => {
    if (!token) return
    stoppedRef.current = false
    setState(initialState)

    fetchStatus(token)
    intervalRef.current = setInterval(() => {
      fetchStatus(token)
    }, POLLING_INTERVAL_MS)
    longMessageTimerRef.current = setTimeout(() => {
      setState((prev) => ({ ...prev, showLongRunningMessage: true }))
    }, LONG_MESSAGE_MS)
    timeoutTimerRef.current = setTimeout(() => {
      setState((prev) => (prev.status === 'completed' ? prev : { ...prev, status: 'timeout' }))
      stopPolling()
    }, TIMEOUT_MS)

    return () => {
      stopPolling()
    }
  }, [token, fetchStatus, stopPolling])

  return state
}
